import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { defineMessages, useIntl } from 'react-intl';
import styled from 'styled-components';
import {
  Editor,
  EditorState,
  Modifier,
  ContentState,
  ContentBlock,
  genKey,
  BlockMapBuilder,
  convertToRaw,
} from 'draft-js';

import Alert from 'components/shared/DesignSystem/Alert';

const Container = styled.div`
  width: 100%;
  border: 1px solid var(--cc_color_border_default);

  .DraftEditor-root {
    padding: 16px 16px 4px;
    width: 100%;

    .public-DraftEditorPlaceholder-root {
      left: 39px;
      top: 16px;
      font-size: 14px;
      line-height: 18px;
    }

    .DraftEditor-editorContainer {
      max-height: 100%;
      overflow: visible;

      li {
        position: relative;
        padding-left: 22px;
        margin: 0 0 12px;
        font-size: 14px;
        line-height: 18px;
        list-style: none;
        cursor: text;

        &::before {
          content: '';
          position: absolute;
          top: 2px;
          left: 0;
          width: 14px;
          height: 14px;
          border: 1px solid var(--cc_color_border_input_inactive);
          border-radius: 2px;
        }

        .public-DraftStyleDefault-block.public-DraftStyleDefault-ltr {
          display: inline;
        }
      }
    }
  }
`;

const AlertStyled = styled(Alert)`
  margin: var(--cc_size_spacing_s) 0;

  i {
    margin-inline-end: 6px;
  }
`;

const messages = defineMessages({
  multiAnswer: {
    id: 'Project.taskList.subTasks.multiSelect.multiAnswer',
    defaultMessage: 'Multi Answer',
    description: 'Multi-Select header option multi answer for type',
  },
  singleAnswer: {
    id: 'Project.taskList.subTasks.multiSelect.sngleAnswer',
    defaultMessage: 'Single Answer',
    description: 'Multi-Select header option single answer for type',
  },
  placeholder: {
    id: 'Project.taskList.subTasks.multiSelect.placeholder',
    defaultMessage: 'Add an Option',
    description: 'Placeholder text for subtask multi-select',
  },
  comingSoon: {
    id: 'Projects.taskList.subTasks.multiSelect.comingSoon',
    defaultMessage: 'Coming Soon',
    description: 'Coming soon badge',
  },
  answerChoiceAlert: {
    id: 'Projects.taskList.subTasks.multiSelect.answerChoiceAlert',
    defaultMessage:
      'Editing options will clear any existing answer selections.',
    description: 'Alert message for user when changing choices',
  },
});

const blockRenderMap = new Map();

blockRenderMap.set('unordered-list-item', { element: 'li' });
blockRenderMap.set('unstyled', { element: 'li' });

const MultiSelectEditor = ({ subtask, onBlur }) => {
  const { formatMessage } = useIntl();
  const [editorState, setEditorState] = useState(() =>
    EditorState.createEmpty(),
  );
  const isEditorEmpty = ContentState(editorState.getCurrentContent()).hasText();

  const createContentBlock = (text) => {
    return new ContentBlock({
      key: genKey(),
      type: 'unordered-list-item',
      text,
    });
  };

  useEffect(
    () => {
      let contentState;

      if (!subtask.answerOptions) {
        contentState = ContentState.createFromText('');
      } else {
        contentState = ContentState.createFromBlockArray(
          subtask.answerOptions.map((answerOption) =>
            createContentBlock(answerOption),
          ),
        );
      }
      setEditorState(EditorState.createWithContent(contentState));
    },
    [subtask.answerOptions],
  );

  return (
    <>
      <Container hasText={isEditorEmpty}>
        <Editor
          ariaLabel="multi-select question editor"
          editorState={editorState}
          placeholder={formatMessage(messages.placeholder)}
          onChange={setEditorState}
          handlePastedText={(text, html) => {
            if (html) {
              const bulletedItemsFromTextPaste = text.split(/\r?\n/);
              const contentBlocks = [];

              bulletedItemsFromTextPaste.forEach((bulletedItem) => {
                if (bulletedItem === '') return;

                const newContentBlock = new createContentBlock(bulletedItem);

                contentBlocks.push(newContentBlock);
              });

              const blockMap = BlockMapBuilder.createFromArray(contentBlocks);
              const newContent = Modifier.replaceWithFragment(
                editorState.getCurrentContent(),
                editorState.getSelection(),
                blockMap,
              );

              setEditorState(
                EditorState.push(editorState, newContent, 'insert-fragment'),
              );

              return true;
            }

            return false;
          }}
          onBlur={() => {
            const { blocks } = convertToRaw(editorState.getCurrentContent());
            const updatedAnswerOptions = blocks
              .map((answerOption) => answerOption.text.trim())
              .filter((answerOption) => answerOption !== '');

            onBlur(updatedAnswerOptions);
          }}
          blockRenderMap={blockRenderMap}
        />
      </Container>
      {subtask.answerChoices?.length ? (
        <AlertStyled
          flavor="caution"
          layout="inline-mini"
          iconName="alert-circle"
        >
          {formatMessage(messages.answerChoiceAlert)}
        </AlertStyled>
      ) : null}
    </>
  );
};

MultiSelectEditor.propTypes = {
  subtask: PropTypes.shape({
    id: PropTypes.string,
    answerOptions: PropTypes.arrayOf(PropTypes.string).isRequired,
    answerChoices: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  onBlur: PropTypes.func.isRequired,
};

export default MultiSelectEditor;
